import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, getDocs, query } from 'firebase/firestore';
import { db, auth } from "shared/utils/firebase";
import { Sun, Moon, ArrowLeft, ExternalLink } from 'react-feather';
import styles from './Examples.module.css';
import UserProfileDropdown from 'shared/components/Auth/UserProfileDropdown';

const DEFAULT_EXAMPLES = [
  {
    id: 'bus_protocol_constraints',
    title: 'Bus Protocol Constraints',
    language: 'SystemVerilog',
    topic: 'Constraints',
    description: 'Simple example on how to create a bus transaction object using SystemVerilog rand variables and alignments constraints.',
    reference: 'Ch#7 : Constraints',
    referenceUrl: 'https://www.chipverify.com/systemverilog/systemverilog-constraint-examples',
    designTemplate: `class bus_trans;
    rand bit [31:0] addr;
    rand bit [31:0] data;
    rand bit        write;
    rand bit [2:0]  delay;

    constraint c_addr {
        addr % 4 == 0; // Word-aligned
        addr >= 32'h1000_0000;
        addr <= 32'h2000_0000;
    }

    constraint c_delay {
        delay inside {[1:5]};
    }
endclass

module top;
    initial begin
        bus_trans trans = new();
        repeat(5) begin
            if (trans.randomize()) begin
                $display("Randomized transaction: Addr=0x%h, Data=0x%h, Write=%b, Delay=%d", 
                         trans.addr, trans.data, trans.write, trans.delay);
            end else begin
                $error("Randomization failed");
            end
        end
    end
endmodule
`,
    tbTemplate: `module tb;
  // Testbench is run via top-level Top module
endmodule
`
  },
  {
    id: 'equal_memory_partitions',
    title: 'Equal Memory Partitions',
    language: 'SystemVerilog',
    topic: 'Constraints',
    description: 'Shows how to create equal regions in memory using constraints and randomization rules.',
    reference: 'Ch#7 : Constraints',
    referenceUrl: 'https://www.chipverify.com/systemverilog/systemverilog-constraint-examples',
    designTemplate: `class memory_partition;
    rand bit [31:0] start_addr [4];
    rand bit [31:0] size;

    constraint c_partitions {
        size == 32'h0100; // 256 bytes each
        foreach (start_addr[i]) {
            if (i > 0) {
                start_addr[i] == start_addr[i-1] + size;
            }
        }
        start_addr[0] == 32'h0000;
    }
endclass

module top;
    initial begin
        memory_partition mem = new();
        if (mem.randomize()) begin
            foreach (mem.start_addr[i]) begin
                $display("Partition %d: StartAddr=0x%h, EndAddr=0x%h", 
                         i, mem.start_addr[i], mem.start_addr[i] + mem.size - 1);
            end
        end
    end
endmodule
`,
    tbTemplate: `module tb;
endmodule
`
  },
  {
    id: 'memory_block_randomization',
    title: 'Memory Block Randomization',
    language: 'SystemVerilog',
    topic: 'Constraints',
    description: 'Shows how constraints can be used to reserve a region in memory while avoiding overlap.',
    reference: 'Ch#7 : Constraints',
    referenceUrl: 'https://www.chipverify.com/systemverilog/systemverilog-constraint-examples',
    designTemplate: `class mem_block;
    rand bit [31:0] base_addr;
    rand bit [31:0] size;
    
    constraint c_range {
        base_addr inside {[32'h0000 : 32'hFFFF]};
        size inside {128, 256, 512, 1024};
        (base_addr + size) <= 32'hFFFF;
    }
endclass

module top;
    initial begin
        mem_block block = new();
        repeat(3) begin
            if (block.randomize()) begin
                $display("Reserved block: Base=0x%h, Size=%d bytes", block.base_addr, block.size);
            end
        end
    end
endmodule
`,
    tbTemplate: `module tb;
endmodule
`
  },
  {
    id: 'variable_memory_partitions',
    title: 'Variable Memory Partitions',
    language: 'SystemVerilog',
    topic: 'Constraints',
    description: 'Shows how to create memory regions of different sizes using rand arrays.',
    reference: 'Ch#7 : Constraints',
    referenceUrl: 'https://www.chipverify.com/systemverilog/systemverilog-constraint-examples',
    designTemplate: `class var_partition;
    rand bit [15:0] sizes [4];
    
    constraint c_var_sizes {
        foreach (sizes[i]) {
            sizes[i] inside {64, 128, 256, 512};
        }
        sizes.sum() == 1024;
    }
endclass

module top;
    initial begin
        var_partition part = new();
        if (part.randomize()) begin
            $display("Partitions size sum: %d", part.sizes.sum());
            foreach (part.sizes[i]) begin
                $display("  Partition %d size: %d bytes", i, part.sizes[i]);
            end
        end
    end
endmodule
`,
    tbTemplate: `module tb;
endmodule
`
  },
  {
    id: 'associative_array_iterators',
    title: 'Associative Array Iterators',
    language: 'SystemVerilog',
    topic: 'Data Types',
    description: 'Simple examples of associative array methods including first, next, and last iterators.',
    reference: 'ChipVerify Ch#3 Assoc Arrays',
    referenceUrl: 'https://www.chipverify.com/systemverilog/systemverilog-associative-array',
    designTemplate: `module top;
    int array [string];
    string index;

    initial begin
        array["apple"]  = 10;
        array["banana"] = 20;
        array["cherry"] = 30;

        if (array.first(index)) begin
            do begin
                $display("Key: %s, Value: %d", index, array[index]);
            end while (array.next(index));
        end
    end
endmodule
`,
    tbTemplate: `module tb;
endmodule
`
  },
  {
    id: 'associative_array_methods',
    title: 'Associative Array Methods',
    language: 'SystemVerilog',
    topic: 'Data Types',
    description: 'How to use in-built methods of associative arrays such as num(), exists(), and delete().',
    reference: '',
    referenceUrl: '',
    designTemplate: `module top;
    int mem [bit [7:0]];

    initial begin
        mem[8'h05] = 100;
        mem[8'h10] = 200;
        
        $display("Number of elements: %d", mem.num());
        if (mem.exists(8'h05)) begin
            $display("Address 8'h05 exists, value = %d", mem[8'h05]);
        end
        
        mem.delete(8'h05);
        $display("After deleting, elements: %d", mem.num());
    end
endmodule
`,
    tbTemplate: `module tb;
endmodule
`
  },
  {
    id: 'associative_arrays',
    title: 'Associative Arrays',
    language: 'SystemVerilog',
    topic: 'Data Types',
    description: 'Create and display associative arrays where keys and values are of different types: https://www.chipverify.com/systemverilog/systemverilog-associative-array',
    reference: '',
    referenceUrl: '',
    designTemplate: `module top;
    // Associative array indexed by string
    int goods [string];
    
    initial begin
        goods["Pen"] = 50;
        goods["Notebook"] = 120;
        $display("Pen price: %d", goods["Pen"]);
        $display("Notebook price: %d", goods["Notebook"]);
    end
endmodule
`,
    tbTemplate: `module tb;
endmodule
`
  },
  {
    id: 'copy_dynamic_array',
    title: 'Copy Dynamic Array',
    language: 'SystemVerilog',
    topic: 'Data Types',
    description: 'How to copy dynamic arrays in SystemVerilog using deep copy syntaxes.',
    reference: '',
    referenceUrl: '',
    designTemplate: `module top;
    int src [];
    int dst [];

    initial begin
        src = new[3];
        src = '{1, 2, 3};
        
        dst = src; // Copying reference
        $display("dst[1] = %d", dst[1]);
        
        dst[1] = 99;
        $display("src[1] after dst modify: %d", src[1]);
    end
endmodule
`,
    tbTemplate: `module tb;
endmodule
`
  },
  {
    id: 'verilog_counter',
    title: 'Counter 4-bit',
    language: 'Verilog',
    topic: 'Sequential',
    description: 'A simple 4-bit synchronous up-counter example with reset and enable triggers.',
    reference: 'Verilog Basics',
    referenceUrl: '',
    designTemplate: `module verilog_counter (
    input  wire clk,
    input  wire rst_n,
    input  wire enable,
    output reg [3:0] count
);

    always @(posedge clk or negedge rst_n) begin
        if (!rst_n) begin
            count <= 4'b0000;
        end else if (enable) begin
            count <= count + 1'b1;
        end
    end

endmodule
`,
    tbTemplate: `module tb_verilog_counter;
    reg clk;
    reg rst_n;
    reg enable;
    wire [3:0] count;

    verilog_counter uut (
        .clk(clk),
        .rst_n(rst_n),
        .enable(enable),
        .count(count)
    );

    initial begin
        clk = 0;
        forever #5 clk = ~clk;
    end

    initial begin
        rst_n = 0;
        enable = 0;
        #20 rst_n = 1;
        #10 enable = 1;
        #100;
        $finish;
    end
endmodule
`
  }
];

const Examples = ({ userDetails, auth: initialAuth, theme, toggleTheme }) => {
  const navigate = useNavigate();
  
  // State
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth);
  const [examplesList, setExamplesList] = useState(DEFAULT_EXAMPLES);
  
  // Search & Filter
  const [activeFilter, setActiveFilter] = useState('All'); // 'All' | 'Verilog' | 'SystemVerilog'
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch examples from Firestore if available
  useEffect(() => {
    const fetchFirestoreExamples = async () => {
      try {
        const q = query(collection(db, 'codeExamples'));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const items = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setExamplesList(items);
        }
      } catch (err) {
        console.error("Firestore examples fetch error (using local default fallback):", err);
      }
    };
    fetchFirestoreExamples();
  }, []);

  // Redirect to login if user logs out
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);
  // Filter and Search calculations
  const counts = useMemo(() => {
    const countsMap = { All: 0, Verilog: 0, SystemVerilog: 0 };
    examplesList.forEach((ex) => {
      countsMap.All++;
      if (ex.language === 'Verilog') countsMap.Verilog++;
      if (ex.language === 'SystemVerilog') countsMap.SystemVerilog++;
    });
    return countsMap;
  }, [examplesList]);

  const filteredExamples = useMemo(() => {
    return examplesList.filter((ex) => {
      const matchesLanguage = 
        activeFilter === 'All' || 
        ex.language.toLowerCase() === activeFilter.toLowerCase();
      
      const matchesSearch = 
        ex.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ex.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ex.description.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesLanguage && matchesSearch;
    });
  }, [activeFilter, searchQuery, examplesList]);

  const handleOpenExampleInWorkspace = (example) => {
    // Write templates to local storage
    localStorage.setItem('active_example', JSON.stringify({
      id: example.id,
      title: example.title,
      designTemplate: example.designTemplate,
      tbTemplate: example.tbTemplate,
      language: example.language
    }));
    navigate('/ide');
  };

  return (
    <div className={`${styles.examplesContainer} ${theme === 'light' ? styles.lightTheme : ''}`}>
      {/* Brand Header */}
      <header className={styles.headerBar}>
        <div className={styles.logoContainer} onClick={() => navigate('/')} title="Back to home">
          <img src="/static_img/weblogo.png" alt="SiliconLogix Logo" className={styles.webLogo} />
        </div>

        <div className={styles.navActions}>
          <button className={styles.themeToggleBtn} onClick={toggleTheme} title="Switch theme">
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          {isAuthenticated && (
            <UserProfileDropdown userDetails={userDetails} styles={styles} />
          )}
        </div>
      </header>

      <main className={styles.mainContent}>
        {/* Back Navigation */}
        <button className={styles.btnNavBack} onClick={() => navigate('/')}>
          <ArrowLeft size={13} />
          <span>Back to Home</span>
        </button>

        {/* Hero Section */}
        <div className={styles.heroSection}>
          <div className={styles.snippetsBadge}>
            <span className={styles.badgeDot}></span>
            Ready-to-run snippets
          </div>
          <h1 className={styles.heroTitle}>
            Code <span className={styles.gradientText}>Examples</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Ready-to-run Verilog, SystemVerilog, and UVM examples. Click any to open it live in the IDE.
          </p>
        </div>

        {/* Filters and Search Control panel */}
        <div className={styles.controlsRow}>
          <div className={styles.filterPillsGroup}>
            <button
              className={`${styles.filterPill} ${activeFilter === 'All' ? styles.filterPillActive : ''}`}
              onClick={() => setActiveFilter('All')}
            >
              All <span className={styles.pillCount}>{counts.All}</span>
            </button>
            <button
              className={`${styles.filterPill} ${activeFilter === 'Verilog' ? styles.filterPillActive : ''}`}
              onClick={() => setActiveFilter('Verilog')}
            >
              Verilog <span className={styles.pillCount}>{counts.Verilog}</span>
            </button>
            <button
              className={`${styles.filterPill} ${activeFilter === 'SystemVerilog' ? styles.filterPillActive : styles.svPillInactive}`}
              onClick={() => setActiveFilter('SystemVerilog')}
            >
              SystemVerilog <span className={styles.pillCount}>{counts.SystemVerilog}</span>
            </button>
          </div>

          <div className={styles.searchWrapper}>
            <input
              type="text"
              placeholder="Search examples..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Category Header Banner */}
        <div className={styles.categoryHeaderCard}>
          <span className={styles.categoryTitle}>
            SYSTEMVERILOG
          </span>
          <span className={styles.categoryCountBadge}>
            {filteredExamples.length} {filteredExamples.length === 1 ? 'EXAMPLE' : 'EXAMPLES'}
          </span>
        </div>

        {/* Data Table */}
        <div className={styles.tableWrapper}>
          <table className={styles.examplesTable}>
            <thead>
              <tr>
                <th style={{ width: '25%' }}>NAME</th>
                <th style={{ width: '15%' }}>TOPIC ⬆</th>
                <th style={{ width: '35%' }}>DESCRIPTION</th>
                <th style={{ width: '15%' }}>REFERENCE</th>
                <th style={{ width: '10%' }}>OPEN</th>
              </tr>
            </thead>
            <tbody>
              {filteredExamples.map((example) => (
                <tr key={example.id}>
                  <td className={styles.nameCell}>{example.title}</td>
                  <td className={styles.topicCell}>{example.topic}</td>
                  <td className={styles.descCell}>
                    {example.description.includes('https://') ? (
                      <>
                        {example.description.split('https://')[0]}
                        <a 
                          href={`https://${example.description.split('https://')[1]}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.inlineLink}
                        >
                          https://{example.description.split('https://')[1]}
                        </a>
                      </>
                    ) : (
                      example.description
                    )}
                  </td>
                  <td className={styles.refCell}>
                    {example.reference ? (
                      <a 
                        href={example.referenceUrl || '#'} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.referenceLink}
                      >
                        {example.reference}
                      </a>
                    ) : (
                      <span style={{ opacity: 0.3 }}>-</span>
                    )}
                  </td>
                  <td>
                    <button 
                      className={styles.btnOpen}
                      onClick={() => handleOpenExampleInWorkspace(example)}
                    >
                      <ExternalLink size={13} style={{ marginRight: '6px' }} />
                      Open
                    </button>
                  </td>
                </tr>
              ))}
              {filteredExamples.length === 0 && (
                <tr>
                  <td colSpan="5" className={styles.emptyCell}>
                    No matching code examples found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default Examples;
